'use client';

import { InputField, SelectField } from '@prenuvo/halo-web';
import { getGenderSelectClassName } from '@/utils/MemberInformation';
import { useMask } from '@react-input/mask';
import styles from './MemberInfoForm.module.css';
import PhoneInputField from './PhoneInputField';
import { useMemberInfoForm } from '@/hooks';

export default function MemberInfoForm() {
  const { setMemberInfo, getMemberInfo, removeError, selectedGender, errorsPatientDetails } =
    useMemberInfoForm();

  const dateOfBirthRef = useMask({
    mask: '__/__/____',
    replacement: { _: /\d/ },
  });

  const memberInfo = getMemberInfo();

  const handleChange = (field: string, value: any) => {
    removeError(field);
    setMemberInfo({ [field]: value });
  };

  const biologicalSexOptions = [
    {
      value: 'male',
      children: <p>Male</p>,
    },
    {
      value: 'female',
      children: <p>Female</p>,
    },
  ];

  const handleLabelClick = () => {
    const selectElement =
      document.querySelector('[data-testid="gender"] select') ||
      document.querySelector('[data-testid="gender"]');
    (selectElement as HTMLElement)?.focus();
    (selectElement as HTMLElement)?.click();
  };

  const shouldDisable = (field: string) => {
    return !!memberInfo.isLoggedIn && !!memberInfo?.initialUserInfo[field];
  };

  return (
    <div>
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
        <div>
          <InputField
            label={{
              children: 'Legal first name',
            }}
            placeholder={{
              children: 'i.e. John',
            }}
            size="lg"
            type="text"
            variant="outlined"
            name="firstName"
            onChange={(e) => handleChange('firstName', e.target.value)}
            isInvalid={!!errorsPatientDetails?.firstName}
            caption={{
              translationKey: errorsPatientDetails?.firstName,
            }}
            value={memberInfo?.firstName}
            isDisabled={shouldDisable('firstName')}
          />
        </div>
        <div>
          <InputField
            label={{
              children: 'Legal Last name',
            }}
            placeholder={{
              children: 'i.e. Smith',
            }}
            size="lg"
            type="text"
            variant="outlined"
            onChange={(e) => handleChange('lastName', e.target.value)}
            name="lastName"
            isInvalid={!!errorsPatientDetails?.lastName}
            caption={{
              translationKey: errorsPatientDetails?.lastName,
            }}
            value={memberInfo?.lastName}
            isDisabled={shouldDisable('lastName')}
          />
        </div>
        <div>
          <InputField
            label={{
              children: 'Preferred name',
            }}
            placeholder={{
              children: 'i.e. John Smith',
            }}
            size="lg"
            type="text"
            variant="outlined"
            onChange={(e) => handleChange('preferredName', e.target.value)}
            name="preferredName"
            isInvalid={!!errorsPatientDetails?.preferredName}
            caption={{
              translationKey: errorsPatientDetails?.preferredName,
            }}
            value={memberInfo?.preferredName}
            isDisabled={shouldDisable('preferredName')}
          />
        </div>
        <div className="relative">
          {/* TODO: add placeholder */}
          <SelectField
            data-testid="gender"
            onChange={(value: string | undefined) => handleChange('gender', value)}
            value={selectedGender}
            name="gender"
            options={biologicalSexOptions}
            isInvalid={!!errorsPatientDetails?.gender}
            caption={{
              translationKey: errorsPatientDetails?.gender && 'Biological sex is required',
            }}
            defaultValue={memberInfo?.gender}
            disabled={shouldDisable('gender')}
            className={{
              root: getGenderSelectClassName({
                styles,
                selected: selectedGender,
                error: !!errorsPatientDetails?.gender,
              }),
            }}
          />
          <label
            className="absolute left-[17px] top-2 font-lato text-xs font-normal leading-[1.05rem] text-neutral-600 transition-all dark:text-neutral-500"
            htmlFor="gender"
            onClick={handleLabelClick}
          >
            Biological Sex
          </label>
        </div>
        <div>
          <InputField
            label={{
              children: 'Date of birth',
            }}
            placeholder={{
              children: 'MM/DD/YYYY',
            }}
            inputMode="numeric"
            onChange={(e) => handleChange('dob', e.target.value)}
            name="dob"
            autoComplete="bday"
            isInvalid={!!errorsPatientDetails?.dob}
            caption={{
              translationKey: errorsPatientDetails?.dob,
            }}
            value={memberInfo?.dob || undefined}
            isDisabled={shouldDisable('dob')}
            ref={dateOfBirthRef}
          />
        </div>
        <div>
          <InputField
            label={{
              children: 'Email',
            }}
            placeholder={{
              children: 'i.e. john.smith@email.com',
            }}
            size="lg"
            type="email"
            variant="outlined"
            onChange={(e) => handleChange('email', e.target.value)}
            name="email"
            aria-required="true"
            aria-invalid={errorsPatientDetails.email ? 'true' : 'false'}
            autoComplete="email"
            isInvalid={!!errorsPatientDetails?.email}
            caption={{
              translationKey: errorsPatientDetails?.email,
            }}
            value={memberInfo?.email}
            isDisabled={shouldDisable('email')}
          />
        </div>
        <div>
          <PhoneInputField
            value={memberInfo?.phoneNumber}
            disabled={shouldDisable('phoneNumber')}
            onChange={handleChange}
            error={errorsPatientDetails?.phoneNumber}
            placeholder="Phone number"
            name="phoneNumber"
          />
        </div>
      </div>
    </div>
  );
}
